import Button from "@ui-kit/form/Button/Button.web";
import Icon from "@ui-kit/icons/Icon.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import HighlightBox from "@ui-kit/layout/HighlightBox/HighlightBox.web";
import Tooltip from "@ui-kit/overlays/Tooltip/Tooltip.web";
import { useTranslation } from "@shared/utils/internationalization";
import { enums, enumsTypes } from "@shared/constants";
import Accordion from "@ui-kit/disclosure/Accordion/Accordion.web";
import { TicketBasketType } from "@shared/modules/basket/types";
import { useCallback, useState } from "react";
import Discount from "../Discount/Discount.web";
import type { AppliedDiscountsSharedProps } from "./AppliedDiscounts.props";
import {
  getDiscountApplication,
  getDiscountLimit,
  getIsAutoDiscount,
  getIsMembershipBenefitDiscount,
  getIsTicketCredit,
} from "../../utils/discount-utils";

const CREDIT_TYPES = new Map(
  enums.SHOWPASS_CREDIT_TYPES_BY_LIST.map((creditType) => [
    creditType.id,
    creditType.name,
  ])
);

export type AppliedDiscountsProps = AppliedDiscountsSharedProps & {
  // Add any web specific props here.
};

export default function AppliedDiscounts(
  props: AppliedDiscountsProps
): JSX.Element | null {
  const { appliedDiscounts, onRemoveDiscounts } = props;
  const { discount, discounts_applied } = appliedDiscounts;
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleRemoveDiscounts = useCallback(async () => {
    try {
      setIsLoading(true);
      await onRemoveDiscounts();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, [onRemoveDiscounts, setIsLoading]);

  if (discount && !discounts_applied?.length) {
    return (
      <AppliedDiscounts.Single
        {...props}
        onRemoveDiscounts={handleRemoveDiscounts}
        isLoading={isLoading}
      />
    );
  }

  if (discounts_applied?.length) {
    return (
      <AppliedDiscounts.Multiple
        {...props}
        onRemoveDiscounts={handleRemoveDiscounts}
        isLoading={isLoading}
      />
    );
  }

  return null;
}

export const SingleDiscount = ({
  appliedDiscounts,
  onRemoveDiscounts,
  isLoading,
}: AppliedDiscountsProps & { isLoading: boolean }) => {
  const { discount, discount_rule } = appliedDiscounts;
  const { t } = useTranslation("discounts");
  const isAutoDiscount = getIsAutoDiscount(discount);
  const isMembershipBenefit = getIsMembershipBenefitDiscount(discount);
  const isTicketCredit = getIsTicketCredit(discount);
  const discountLimit = getDiscountLimit(discount);
  const discountApplication = getDiscountApplication(discount);

  if (isAutoDiscount) {
    return (
      <Discount
        code={discount.description}
        amount={Number(discount_rule?.amount)}
        percentage={Number(discount_rule?.percentage)}
        application={discountApplication}
        limit={discountLimit}
      />
    );
  }

  if (isMembershipBenefit) {
    return (
      <Discount
        code={discount.code}
        amount={Number(discount?.amount)}
        percentage={Number(discount?.percentage)}
        application={discountApplication}
        limit={discountLimit}
        applied={t("Membership benefit")}
      />
    );
  }

  if (isTicketCredit) {
    return (
      <Discount
        code={discount.code}
        description={discount?.description}
        amount={Number(discount?.amount)}
        percentage={Number(discount?.percentage)}
        application={discountApplication}
        limit={discountLimit}
        onClick={onRemoveDiscounts}
        isDisabled={isLoading}
        applied={t("Credit applied")}
      />
    );
  }

  return (
    <Discount
      code={discount.code}
      description={discount?.description}
      amount={Number(discount?.amount)}
      percentage={Number(discount?.percentage)}
      application={discountApplication}
      limit={discountLimit}
      onClick={onRemoveDiscounts}
      isDisabled={isLoading}
    />
  );
};

AppliedDiscounts.Single = SingleDiscount;

export const MultipleDiscounts = ({
  appliedDiscounts,
  onRemoveDiscounts,
  isLoading,
}: AppliedDiscountsProps & { isLoading: boolean }) => {
  const { discounts_applied } = appliedDiscounts;
  const { t } = useTranslation("discounts");
  const canRemove = discounts_applied?.some(
    (discount) =>
      discount.type !== enums.DISCOUNT_TYPES.DISC_TYPE_AUTO_APPLIED.id &&
      discount.type !== enums.DISCOUNT_TYPES.DISC_TYPE_MEMBERSHIP.id
  );

  return (
    <Accordion collapsible bg="green.200" p="none" borderRadius="sm">
      <Accordion.Item border="unset" value="discounts">
        {/* eslint-disable-next-line react/no-unused-prop-types */}
        {({ isExpanded }: { isExpanded: boolean }) => (
          <>
            <Accordion.Trigger
              iconPosition="none"
              p="xs"
              border="none"
              _hover={{ backgroundColor: "transparent" }}
            >
              <Flex width="100%" justify="space-between" align="center">
                <Text
                  color="green.900"
                  size="xs"
                  lineHeight="17px"
                  fontWeight={700}
                >
                  {discounts_applied?.length}
                  {` `}
                  {t("discounts applied")}
                </Text>
                <Flex align="center" p="4px 8px" gap="2xs">
                  <Text
                    color="green.900"
                    size="sm"
                    fontWeight="500"
                    textDecoration="underline"
                    _hover={{
                      textDecoration: "none",
                    }}
                  >
                    {t("View discounts")}
                  </Text>
                  {isExpanded ? (
                    <Icon.ChevronDown.Solid
                      width="element.md"
                      height="element.md"
                      color="green.900"
                    />
                  ) : (
                    <Icon.ChevronUp.Solid
                      width="element.md"
                      height="element.md"
                      color="green.900"
                    />
                  )}
                </Flex>
              </Flex>
            </Accordion.Trigger>
            <Accordion.Content p="none">
              <Flex
                direction="column"
                maxHeight="element.16xl"
                overflowY="auto"
              >
                {discounts_applied &&
                  discounts_applied.map((discount, index) => {
                    const isMembershipBenefit =
                      getIsMembershipBenefitDiscount(discount);
                    const isTicketCredit = getIsTicketCredit(discount);
                    const discountLimit = getDiscountLimit(discount);
                    const discountApplication =
                      getDiscountApplication(discount);

                    if (isMembershipBenefit) {
                      return (
                        <Discount
                          key={discount.code}
                          code={discount.code}
                          amount={Number(discount?.amount)}
                          percentage={Number(discount?.percentage)}
                          application={discountApplication}
                          limit={discountLimit}
                          applied={t("Membership benefit")}
                        />
                      );
                    }

                    if (isTicketCredit) {
                      return (
                        <Discount
                          key={discount.code}
                          code={discount.code}
                          description={discount?.description}
                          amount={Number(discount?.amount)}
                          percentage={Number(discount?.percentage)}
                          application={discountApplication}
                          limit={discountLimit}
                          applied={t("Credit applied")}
                        />
                      );
                    }

                    return (
                      <Discount
                        key={discount.code}
                        code={discount.code}
                        description={discount?.description}
                        amount={Number(discount?.amount)}
                        percentage={Number(discount?.percentage)}
                        application={discountApplication}
                        limit={discountLimit}
                        data-testid={`${index}-discount`}
                      />
                    );
                  })}
              </Flex>
              {canRemove && (
                <Button.Link
                  display="flex"
                  color="green.900"
                  size="sm"
                  fontWeight="500"
                  margin="8px auto 0"
                  padding="xs"
                  onClick={onRemoveDiscounts}
                  aria-label={t("remove discounts")}
                  data-testid="remove-discount-codes"
                  isDisabled={isLoading}
                  _hover={{
                    textDecoration: "none",
                  }}
                >
                  {t("Remove discounts")}
                </Button.Link>
              )}
            </Accordion.Content>
          </>
        )}
      </Accordion.Item>
    </Accordion>
  );
};

AppliedDiscounts.Multiple = MultipleDiscounts;

AppliedDiscounts.Item = function AppliedDiscount({
  discount,
  discountsApplied,
  "data-testid": dataTestId,
}: {
  discount?: string;
  discountsApplied?: TicketBasketType["discounts_applied"];
  "data-testid"?: string;
}) {
  const { t } = useTranslation("discounts");

  if (discountsApplied && discountsApplied.length > 1) {
    return (
      <Flex>
        <Tooltip
          label={
            <Box>
              {discountsApplied.map((discount) => (
                <Text color="bg" key={discount.code}>
                  {discount.code}
                </Text>
              ))}
            </Box>
          }
          data-testid="test"
          placement="top"
          hasArrow
        >
          <HighlightBox
            statusColor="green"
            border="none"
            ml="auto"
            w="auto"
            p="3px 8px"
            cursor="pointer"
          >
            <Text color="green.900" fontWeight={600}>
              {t("Multiple discounts applied")}
            </Text>
          </HighlightBox>
        </Tooltip>
      </Flex>
    );
  }

  return discount ? (
    <Flex>
      <HighlightBox
        statusColor="green"
        border="none"
        ml="auto"
        w="auto"
        p="3px 8px"
      >
        <Text color="green.900" fontWeight={600}>
          {t("Code")}
          {`: `}
          <Text
            as="span"
            color="green.900"
            data-testid={dataTestId}
            wordBreak="break-all"
          >
            {discount}
          </Text>
        </Text>
      </HighlightBox>
    </Flex>
  ) : null;
};

AppliedDiscounts.Credit = function AppliedDiscountCredit({
  creditType,
  "data-testid": dataTestId,
}: {
  creditType?: enumsTypes.ShowpassCreditTypesMapIdType;
  "data-testid"?: string;
}) {
  if (!creditType || !CREDIT_TYPES.has(creditType)) {
    return null;
  }
  return (
    <Flex>
      <HighlightBox
        statusColor="green"
        border="none"
        ml="auto"
        w="auto"
        p="3px 8px"
      >
        <Text color="green.900" fontWeight={600}>
          <Text
            as="span"
            color="green.900"
            data-testid={dataTestId}
            wordBreak="break-all"
          >
            {CREDIT_TYPES.get(creditType)}
          </Text>
        </Text>
      </HighlightBox>
    </Flex>
  );
};
